import { useGetLocalizedLabelsQuery } from '../requests/localization.gql.generated';

export interface LocalizedLabelMap {
  [key: string]: string;
}

/**
 * Hook to fetch localized labels from the backend for a given scope.
 *
 * @param scope - The scope of labels to fetch (e.g., 'borrowingBaseSummary')
 * @returns Object with loading state, error, and labelMap
 */
export const useLocalizedLabels = (scope: string) => {
  const { data, loading, error } = useGetLocalizedLabelsQuery({
    variables: { scope },
    fetchPolicy: 'cache-first', // Cache labels to avoid unnecessary refetches
  });

  // Transform the array of labels into a map for easy lookup
  const labelMap: LocalizedLabelMap = {};

  if (data?.getLocalizedLabels?.labels) {
    data.getLocalizedLabels.labels.forEach((label) => {
      labelMap[label.labelKey] = label.localizedValue;
    });
  }

  return {
    loading,
    error,
    labelMap,
  };
};

/**
 * Helper function to get a localized label from the map, falling back to the default label.
 *
 * @param labelMap - The map of localized labels from useLocalizedLabels
 * @param defaultLabel - The default label to use if no localization exists
 * @returns The localized label or the default label
 */
export const getLocalizedLabel = (
  labelMap: LocalizedLabelMap,
  defaultLabel: string,
): string => {
  return labelMap[defaultLabel] || defaultLabel;
};
